const input = () => {
  return {
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary
        }),
        input: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary
        })
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            // minHeight, not height: a fixed height here clamps multi-value
            // Autocompletes (Tags, Categories) that render their input via
            // `renderInput={params => <TextField {...params} />}` — the chips
            // need to be able to push this box taller when they wrap onto a
            // second line, or they get cut off at the bottom edge instead of
            // pushing it open. minHeight still guarantees every single-line
            // TextField/Select/Autocomplete renders at the same 48px baseline.
            minHeight: '48px',
            backgroundColor: '#FFFFFF',
            '& input': {
              padding: '6px 12px'
            }
          }
          // Label position/color/height is NOT set here. It used to be
          // ('& .MuiInputLabel-root': { top: '15%' }), which is exactly why
          // TextField labels and bare Select/OutlinedInput labels needed two
          // separate positioning systems — they were tuned for two different
          // heights. Now that both are unified at 48px (see MuiOutlinedInput
          // below), MuiInputLabel's own rule handles every label the same
          // way, whether it's inside a TextField, a bare Select, or a bare
          // OutlinedInput. Don't reintroduce a label override here.
        }
      }
    },
    // THE single source of truth for every field label ("title"/"heading")
    // in the app, full stop — TextField, Select, OutlinedInput, Autocomplete,
    // all of it. This one rule is what makes Segment Details, Template
    // setup, Campaign setup, Add User, Add Audience, and the profile page
    // all behave identically. If a label ever needs to look different
    // in the future, this is the one place to change it.
    //
    // This only works because every field's box height is standardized to
    // 48px (MuiTextField above and MuiOutlinedInput below) — top:50% +
    // translateY(-50%) centers a label perfectly regardless of height, so
    // as long as that 48px standard holds, one rule covers every field type
    // with no per-component overrides anywhere.
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Resting (un-shrunk) labels sit inside the field exactly like a
          // native placeholder, so they match the same color as real
          // placeholders (see 'input::placeholder' in globalStyles.js).
          color: theme.palette.text.disabled,
          fontSize: theme.typography.body1.fontSize,
          fontFamily: theme.typography.fontFamily,
          top: '50%',
          transform: 'translate(12px, -50%) scale(1)',
          '&.MuiInputLabel-shrink': {
            // Shrunk into a real caption above the field: pinned to the top
            // edge, reads as a proper (darker) heading — unless focused.
            top: 0,
            color: theme.palette.text.secondary,
            transform: 'translate(12px, -9px) scale(0.80)'
          },
          // Turns primary blue while actively focused (clicking into any
          // field on the Template/Campaign pages does this). Declared last
          // so it wins the specificity tie against '&.MuiInputLabel-shrink'
          // above — both are 2-class selectors that are usually true at the
          // same time (you're typically shrunk *and* focused while typing).
          '&.Mui-focused:not(.Mui-error)': {
            color: theme.palette.primary.main
          }
        })
      }
    },
    // Universal counterpart to MuiInputLabel above, for any bare FormLabel
    // used as a heading/legend outside of a text field (RadioGroup legends,
    // fieldset headings, etc.) — same rule: turn primary blue while the
    // FormControl it belongs to is focused, so the "click it and it goes
    // blue" behavior is consistent everywhere, not just on text inputs.
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused:not(.Mui-error)': {
            color: theme.palette.primary.main
          }
        })
      }
    },
    MuiInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '48px',
          //padding: '6px 12px',
          lineHeight: '1.5rem',
          '&:before': {
            borderBottom: `1px solid rgba(${theme.palette.customColors.main}, 0.22)`
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `1px solid rgba(${theme.palette.customColors.main}, 0.32)`
          },
          '&.Mui-disabled:before': {
            borderBottomStyle: 'solid'
          }
        })
      }
    },
    MuiFilledInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: '36px',
          padding: '6px 12px',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          backgroundColor: `rgba(${theme.palette.customColors.main}, 0.05)`,
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: `rgba(${theme.palette.customColors.main}, 0.08)`
          },
          '&:before': {
            borderBottom: `1px solid rgba(${theme.palette.customColors.main}, 0.22)`
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `1px solid rgba(${theme.palette.customColors.main}, 0.32)`
          },
          '&.Mui-disabled': {
            backgroundColor: `rgba(${theme.palette.customColors.main}, 0.05)`,
            '&:before': {
              borderBottomStyle: 'solid'
            }
          }
        })
      }
    },
    // Single source of truth for every bare Select / OutlinedInput field
    // (background, height, border colors). See select.js for why this must
    // not also be declared there.
    //
    // Height is 48px to match MuiTextField above: TextField forces its
    // '.MuiInputBase-root' to 48px via a MORE SPECIFIC selector
    // ('.MuiTextField-root .MuiInputBase-root', two classes) than this rule
    // ('.MuiOutlinedInput-root', one class), so TextField always wins that
    // fight and renders at 48px regardless of what's set here. Bare
    // Select/OutlinedInput (Select Role, Current Password, Country, etc.)
    // have no such override fighting for them, so THIS is the number that
    // actually determines their height — it must stay in sync with the 48px
    // used by MuiTextField or every plain Select/OutlinedInput will render
    // shorter than the TextFields sitting right next to it.
    //
    // minHeight, not height: this used to be a fixed height: '42px
    // !important', and being !important it beat every other rule in the
    // cascade including MuiAutocomplete's own `minHeight`-only rule in
    // autocomplete.js — which exists specifically so multi-value
    // Autocompletes (Tags, Categories) can grow taller when their chips wrap
    // onto a second line. With a fixed !important height on this shared
    // root, those chips had nowhere to go and got clipped at the bottom
    // edge instead of pushing the field open. minHeight still forces every
    // single-line field to the same 48px baseline (nothing here shrinks
    // below it) while letting multi-line content grow past it.
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: '48px !important',
          backgroundColor: '#FFFFFF',
          borderRadius: 8,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px'
          },
          '&:hover:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: `rgba(${theme.palette.customColors.main}, 0.32)`
          },
          '&:hover.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.error.main
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: `rgba(${theme.palette.customColors.main}, 0.22)`
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.disabled
          }
        }),
        input: {
          padding: '6px 12px'
        }
      }
    }
  }
}

export default input
